import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardPaste, AlertTriangle, Image as ImageIcon, ShieldAlert, Loader2 } from 'lucide-react';
import { getClipboardEvents, getClipboardImage, getClipboardRiskBreakdown } from '../services/api';
import { useScrollToEvent } from '../hooks/useScrollToEvent';
import DateRangeFilter from '../components/DateRangeFilter';
import dayjs from 'dayjs';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { EmptyState } from '../components/ui/empty-state';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../components/ui/modal';
import { cn } from '../lib/utils';

function ClipboardPage() {
  const { workerId } = useParams();
  const [clipboardEvents, setClipboardEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterParams, setFilterParams] = useState({ startDate: null, endDate: null, hours: null });
  const [contentTypeFilter, setContentTypeFilter] = useState('all');
  const [riskOpen, setRiskOpen] = useState(false);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskData, setRiskData] = useState(null);
  const itemsPerPage = 200;
  useScrollToEvent(clipboardEvents);

  const openRiskBreakdown = async (event) => {
    setRiskOpen(true);
    setRiskLoading(true);
    setRiskData(null);
    try {
      const res = await getClipboardRiskBreakdown(event.id);
      setRiskData(res.data);
    } catch (err) {
      console.error('Failed to load risk breakdown:', err);
      setRiskData({ error: 'Failed to load risk breakdown.' });
    } finally {
      setRiskLoading(false);
    }
  };

  const riskTone = (score) => {
    if (score >= 80) return 'destructive';
    if (score >= 50) return 'destructive';
    if (score >= 20) return 'warning';
    return 'secondary';
  };

  useEffect(() => {
    loadClipboardEvents();
  }, [workerId, page, filterParams, contentTypeFilter]);

  const loadClipboardEvents = async () => {
    try {
      setLoading(true);
      const response = await getClipboardEvents(
        itemsPerPage,
        workerId ? parseInt(workerId) : null,
        filterParams.startDate,
        filterParams.endDate,
        contentTypeFilter === 'image' ? 'image' : null
      );
      setClipboardEvents(response.data.data);
      setTotalCount(response.data.count || response.data.data.length);
      setError(null);
    } catch (err) {
      console.error('Failed to load clipboard events:', err);
      setError('Failed to load clipboard events');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (params) => {
    setFilterParams(params);
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader title="Clipboard Activity" description="Monitor clipboard copy/paste operations" />

      <DateRangeFilter onFilterChange={handleFilterChange} />

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Show:</span>
        <Button
          size="sm"
          variant={contentTypeFilter === 'all' ? 'default' : 'outline'}
          onClick={() => setContentTypeFilter('all')}
        >
          All
        </Button>
        <Button
          size="sm"
          variant={contentTypeFilter === 'image' ? 'default' : 'outline'}
          onClick={() => setContentTypeFilter('image')}
        >
          Images only
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Clipboard Events</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
          ) : clipboardEvents.length === 0 ? (
            <EmptyState
              icon={ClipboardPaste}
              title="No clipboard events"
              description="No clipboard events recorded yet. Events will appear here as users copy/paste content."
            />
          ) : (
            <ul className="divide-y divide-border">
              {clipboardEvents.map((event, index) => (
                <li
                  key={event.id ?? index}
                  id={`event-${event.id}`}
                  className={cn(
                    'flex items-start gap-3 p-4',
                    event.content_type === 'image' && 'cursor-pointer hover:bg-muted/50'
                  )}
                  onClick={() => {
                    if (event.content_type === 'image') {
                      setSelectedImage(event);
                      setDialogOpen(true);
                    }
                  }}
                >
                  {event.content_type === 'image' &&
                    (event.image_file ? (
                      <img
                        src={getClipboardImage(event.id)}
                        alt="Clipboard"
                        className={cn(
                          'h-20 w-20 rounded object-cover',
                          event.has_pii ? 'border-2 border-destructive' : 'border border-border'
                        )}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded bg-muted">
                        <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      </div>
                    ))}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex-1 truncate text-sm text-foreground">
                        {event.content_preview || (event.content_type === 'image' ? 'Image' : 'N/A')}
                      </span>
                      {event.content_type === 'image' && (
                        <Badge variant="secondary">
                          <ImageIcon className="h-3 w-3" />
                          IMAGE
                        </Badge>
                      )}
                      {event.has_pii && (
                        <Badge variant={event.content_type === 'image' ? 'destructive' : 'warning'}>
                          <AlertTriangle className="h-3 w-3" />
                          PII
                        </Badge>
                      )}
                      {event.risk_score > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRiskBreakdown(event);
                          }}
                          title="View risk explanation"
                          className="rounded-full transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                        >
                          <Badge variant={riskTone(event.risk_score)}>
                            <ShieldAlert className="h-3 w-3" />
                            Risk: {event.risk_score}
                          </Badge>
                        </button>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {dayjs(event.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Source: {event.source_application} • Type: {event.content_type}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}

      {/* Risk breakdown modal */}
      <Modal open={riskOpen} onClose={() => setRiskOpen(false)} className="max-w-2xl">
        <ModalHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            <ModalTitle>Risk Score Explanation</ModalTitle>
          </div>
        </ModalHeader>
        <ModalBody className="space-y-4">
          {riskLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          {!riskLoading && riskData?.error && (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertDescription>{riskData.error}</AlertDescription>
            </Alert>
          )}
          {!riskLoading && riskData?.breakdown && (
            <>
              {/* Summary card */}
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                      Total risk score
                    </div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-4xl font-bold tabular-nums text-foreground">
                        {riskData.breakdown.risk_score}
                      </span>
                      <span className="text-sm text-muted-foreground">/ 100</span>
                    </div>
                  </div>
                  <Badge variant={riskTone(riskData.breakdown.risk_score)}>
                    {riskData.breakdown.method}
                  </Badge>
                </div>
                <p className="mt-3 text-sm text-foreground">
                  {riskData.breakdown.explanation}
                </p>
              </div>

              {/* Per-match table */}
              {riskData.breakdown.matches?.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">Indicator</th>
                        <th className="px-3 py-2">Detected value</th>
                        <th className="px-3 py-2">Method</th>
                        <th className="px-3 py-2 text-right">Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {riskData.breakdown.matches.map((m, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-medium text-foreground">{m.label}</td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                            {m.value}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className="text-xs">
                              {m.method}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums text-foreground">
                            +{m.score}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
                  No specific risk indicators detected.
                </p>
              )}

              <div className="text-xs text-muted-foreground">
                Source application:{' '}
                <span className="font-medium text-foreground">
                  {riskData.source_application || '—'}
                </span>
                {' • '}
                Type: <span className="font-medium text-foreground">{riskData.content_type}</span>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setRiskOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <ModalHeader>
          <ModalTitle>Clipboard Image</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-3">
          {selectedImage && (
            <>
              <div className="flex justify-center">
                <img
                  src={getClipboardImage(selectedImage.id)}
                  alt="Clipboard"
                  className="max-h-[70vh] max-w-full object-contain"
                />
              </div>
              <div className="text-sm">
                <div>
                  <strong>Time:</strong> {dayjs(selectedImage.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                </div>
                <div>
                  <strong>Source:</strong> {selectedImage.source_application}
                </div>
              </div>
              {selectedImage.has_pii && (
                <Alert variant="destructive">
                  <AlertTriangle />
                  <AlertDescription>
                    <strong>PII Detected in Image</strong> — This image contains sensitive information
                    that has been highlighted in red.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setDialogOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default ClipboardPage;
